import { parseStringToBoolean } from '@healthhub/react-ui';
import { Typography } from 'antd';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ImmunisationCardImg, LabReportsCardImg, PaymentCardImg } from '@shared/assets';
import { MaxWidthContainer } from '@shared/components';
import {
  BILLS_PATH,
  IMMUNISATION_RECORDS_PATH,
  LAB_TEST_RESULTS_PATH,
  MY_PROFILE_PATH,
  PAYMENT_SERVICE_TOKENIZATION_PATH,
  PAYMENTS_PATH,
} from '@shared/constants/paths';
import { USE_ENETS_PAYMENT } from '@shared/constants/systemSettingsKey';
import { useCurrentUserV2 } from '@shared/hooks/useCurrentUserV2';
import { useSystemSettingsValue } from '@shared/hooks/useSystemSettingsValue';
import { useGetInvoiceOutstandingTotalQuery } from '@shared/services/bills';
import { useGetAllClusterBillsQuery } from '@shared/services/payments';
import { getDisplayName } from '@shared/utils/displayName';
import { formatPayment } from '@shared/utils/string';

import { DashboardProfile } from '@features/Home/shared/components';

import AppointmentCard from './AppointmentCard';
import PostLoginDashboardCard from './PostLoginDashboardCard';

const { Text } = Typography;

const PostLoginDashboard = () => {
  const { t } = useTranslation('home');
  const { userProfile } = useCurrentUserV2({ isPatientMasterInfoRequired: true });

  const { getValue, isFetching: isFetchingENetsPaymentEnabled } = useSystemSettingsValue([USE_ENETS_PAYMENT]);
  const isENetsPaymentEnabled = parseStringToBoolean(getValue(USE_ENETS_PAYMENT));

  const invoiceOutstandingTotalQuery = useGetInvoiceOutstandingTotalQuery(
    {
      MemberPrn: '',
    },
    { skip: isFetchingENetsPaymentEnabled || isENetsPaymentEnabled }
  );
  const legacyOutstandingTotalQuery = useGetAllClusterBillsQuery(
    {
      MemberPrn: '',
    },
    { skip: isFetchingENetsPaymentEnabled || !isENetsPaymentEnabled }
  );

  const {
    data: paymentData,
    isFetching: isOutstandingTotalFetching,
    refetch: paymentRefetch,
  } = isENetsPaymentEnabled ? legacyOutstandingTotalQuery : invoiceOutstandingTotalQuery;

  const isPaymentFetching = isFetchingENetsPaymentEnabled || isOutstandingTotalFetching;

  const hasOutstandingBill = useMemo(
    () => paymentData?.OutstandingBillAmounts.some((record) => record.Amount > 0),
    [paymentData?.OutstandingBillAmounts]
  );

  const totalOutstandingBill =
    paymentData?.OutstandingBillAmounts.reduce((total, bill) => total + bill.Amount, 0) ?? null;

  return (
    <div className="flex w-full justify-center px-3 py-10 pt-4 md:px-8 lg:px-18">
      <MaxWidthContainer className="flex flex-col gap-10 lg:gap-15">
        <DashboardProfile
          description={t('viewAndManageYourHealthRecords')}
          linkedPaymentMethodUrl={PAYMENT_SERVICE_TOKENIZATION_PATH}
          manageProfileUrl={MY_PROFILE_PATH}
          title={t('yourDashboard')}
          userProfile={{
            name: getDisplayName(userProfile),
            photo: userProfile?.Photo?.Data,
          }}
        />
        <div className="flex flex-col gap-6">
          <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2">
            <AppointmentCard />
            <PostLoginDashboardCard
              button={{
                title: t('viewDetails'),
              }}
              image={LabReportsCardImg}
              title={t('labReports')}
              url={LAB_TEST_RESULTS_PATH}
            >
              <Text className="text-sm font-semibold">{t('viewLabTestResults')}</Text>
            </PostLoginDashboardCard>
          </div>
          <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2">
            {isENetsPaymentEnabled ? (
              <PostLoginDashboardCard
                button={{
                  title: hasOutstandingBill ? t('payOutstandingBill') : t('managePayments'),
                }}
                image={PaymentCardImg}
                isLoading={isPaymentFetching}
                isShowRefreshButton
                onRefreshClick={paymentRefetch}
                title={t('payments')}
                url={PAYMENTS_PATH}
              >
                {hasOutstandingBill ? (
                  <div className="flex flex-col gap-2">
                    {paymentData?.OutstandingBillAmounts.map((record) => (
                      <div className="flex items-center gap-5" key={record.ClusterCode}>
                        <Text className="w-12 text-sm font-semibold">{record.ClusterCode}</Text>
                        <Text className="text-xs font-medium">
                          <Trans i18nKey="home:amountToPay" values={{ amount: formatPayment(record.Amount) }}>
                            Amount to pay:
                            <span className="gbmask text-sm font-semibold text-primary-blue-grey">S$</span>
                          </Trans>
                        </Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  !isPaymentFetching && <Text className="text-sm font-semibold">{t('noOutstandingPayments')}</Text>
                )}
              </PostLoginDashboardCard>
            ) : (
              <PostLoginDashboardCard
                button={{
                  title: hasOutstandingBill ? t('payOutstandingBill') : t('managePayments'),
                }}
                image={PaymentCardImg}
                isLoading={isPaymentFetching}
                isShowRefreshButton
                onRefreshClick={paymentRefetch}
                title={t('payments')}
                url={BILLS_PATH}
              >
                {hasOutstandingBill ? (
                  <Text className="text-sm font-semibold">
                    <Trans
                      i18nKey="home:amountToPay"
                      values={{
                        amount: formatPayment(totalOutstandingBill),
                      }}
                    >
                      Amount to pay: <span className="gbmask h3.5 font-semibold text-primary-blue-grey">S$</span>
                    </Trans>
                  </Text>
                ) : (
                  !isPaymentFetching && <Text className="text-sm font-semibold">{t('noOutstandingPayments')}</Text>
                )}
              </PostLoginDashboardCard>
            )}
            <PostLoginDashboardCard
              button={{
                title: t('viewDetails'),
              }}
              image={ImmunisationCardImg}
              title={t('immunisation')}
              url={IMMUNISATION_RECORDS_PATH}
            >
              <Text className="text-sm font-semibold">{t('checkImmunisationRecords')}</Text>
            </PostLoginDashboardCard>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
};

export default PostLoginDashboard;
